import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import toast from 'react-hot-toast';
import AnalyticsService from '../utils/analytics';
import { getArtifactProjectId } from '../firebaseInit';
import ErrorMessage from './shared/ErrorMessage';
import LoadingSpinner from './shared/LoadingSpinner';
import Button from './shared/Button';
import Card from './shared/Card';
import { SkeletonTable } from './shared/Skeleton';
import { colors, spacing, typography, borderRadius, shadows, transitions, zIndex } from '../styles/designTokens';

// ─── Outcome / Temperature constants (defined once outside component) ─────────

const OUTCOME_TEMPERATURE = {
  'Hot Lead':       'hot',
  'Warm Lead':      'warm',
  'Callback':       'warm',
  'Cold Lead':      'cold',
  'Not Interested': 'cold',
  'Voicemail':      'cold',
  'Wrong Number':   'cold',
  'Do Not Call':    'cold',
};

const OUTCOME_OPTIONS = [
  { value: 'Hot Lead',       label: '🔥 Hot Lead',       temp: 'hot' },
  { value: 'Warm Lead',      label: '🌡️ Warm Lead',      temp: 'warm' },
  { value: 'Callback',       label: '📅 Callback',        temp: 'warm' },
  { value: 'Cold Lead',      label: '❄️ Cold Lead',       temp: 'cold' },
  { value: 'Not Interested', label: '🚫 Not Interested',  temp: 'cold' },
  { value: 'Voicemail',      label: '📬 Voicemail',       temp: 'cold' },
  { value: 'Wrong Number',   label: '❌ Wrong Number',    temp: 'cold' },
  { value: 'Do Not Call',    label: '⛔ Do Not Call',     temp: 'cold' },
];

const OUTCOME_BADGE = {
  'Hot Lead':       { bg: '#FEF3C7', color: '#D97706', border: '#FDE68A' },
  'Warm Lead':      { bg: '#FFF7ED', color: '#EA580C', border: '#FED7AA' },
  'Callback':       { bg: '#EDE9FE', color: '#7C3AED', border: '#DDD6FE' },
  'Cold Lead':      { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
  'Not Interested': { bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB' },
  'Voicemail':      { bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  'Wrong Number':   { bg: '#FEF2F2', color: '#DC2626', border: '#FEE2E2' },
  'Do Not Call':    { bg: '#FFF1F2', color: '#BE123C', border: '#FFE4E6' },
};

// ─── Default lead capture form fields ─────────────────────────────────────────
const DEFAULT_LEAD_CAPTURE_FORM = {
  title: 'Lead Data',
  fields: [
    { id: 'prospectName', label: 'Contact Name', type: 'text', required: true },
    { id: 'prospectPhone', label: 'Phone Number', type: 'tel', required: true },
    { id: 'prospectEmail', label: 'Email Address', type: 'email', required: false },
    { id: 'company', label: 'Company', type: 'text', required: false }
  ]
};

/**
 * Leads - Lead management hub with table view and form configuration
 * 
 * Displays recent leads in a table format with export functionality.
 * Allows team leads/admins to configure the lead capture form fields.
 */
const Leads = ({ 
  db, 
  userId, 
  userEmail, 
  userRole,
  userTeam,
  userTeamName,
  isAdmin,
  isTeamLead,
  availableScripts = {},
  onUpdateTemplate
}) => {
  // Time period filter
  const [timePeriod, setTimePeriod] = useState('today');
  // Script/template filter
  const [selectedScriptFilter, setSelectedScriptFilter] = useState('all');
  
  // Core data state
  const [leadsData, setLeadsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Analytics service
  const [analytics, setAnalytics] = useState(null);

  // Lead capture form state
  const [leadCaptureForm, setLeadCaptureForm] = useState(DEFAULT_LEAD_CAPTURE_FORM);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [isEditingForm, setIsEditingForm] = useState(false);
  const [editedFields, setEditedFields] = useState([]);
  const [savingForm, setSavingForm] = useState(false);

  // Form tab state: 'default' for team default, or a scriptId for per-script editing
  const [activeFormTab, setActiveFormTab] = useState('default');

  // If navigated here from a script detail panel, open the form section for that script
  const location = useLocation();
  useEffect(() => {
    const formScriptId = location.state?.formScriptId;
    if (formScriptId) {
      setIsFormExpanded(true);
      setActiveFormTab(formScriptId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-script form editing state
  const [scriptFormEditing, setScriptFormEditing] = useState(null); // scriptId being edited, or null
  const [scriptEditedFields, setScriptEditedFields] = useState([]);
  const [savingScriptForm, setSavingScriptForm] = useState(false);

  // Sort state (3A)
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Search state (3B)
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Detail panel state (3C)
  const [selectedLead, setSelectedLead] = useState(null);

  // Pagination state (3D)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filter panel state
  const [showFilters, setShowFilters] = useState(false);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');

  // Subscription state
  const [subscriptionPlan, setSubscriptionPlan] = useState('starter');

  // Team member name map (email/emailSlug → display name)
  const [teamMemberNames, setTeamMemberNames] = useState({});

  // Evaluations map (linkedLeadId → evaluation data)
  const [evaluationsMap, setEvaluationsMap] = useState({});
  const [evaluationsList, setEvaluationsList] = useState([]);

  // Time period options
  const timePeriodOptions = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_quarter', label: 'This Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'this_year', label: 'This Year' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'all', label: 'All Time' }
  ];

  const getTemperature = (lead) => OUTCOME_TEMPERATURE[lead.callOutcome] || null;

  // Fields to exclude from dynamic columns (internal/metadata fields)
  const excludedFields = useMemo(() => new Set([
    'id',
    'userId',
    'conversationHistory',
    'propertyDetails',
    'createdBy',
    'creatorEmail',
    'creatorTeam',
    'template',
    'templateName',
    'timestamp',
    'userEmail',
    'userTeam',
    'userTeamName',
    'urgency',
    'leadCategory',
    'agentName',
    'Agent Name',
    'agentDisplayName',
    '_fieldLabels',
    'callSessionId',
    'linkedEvaluationId',
    'callOutcome',
  ]), []);

  // Initialize analytics service
  useEffect(() => {
    if (db && userId && userEmail) {
      const analyticsService = new AnalyticsService(db, userId, userEmail, userTeam, userTeamName, isAdmin);
      setAnalytics(analyticsService);
    }
  }, [db, userId, userEmail, userTeam, userTeamName, isAdmin]);

  // Load team settings (subscription and lead capture form)
  useEffect(() => {
    const loadTeamSettings = async () => {
      if (!db || !userTeam) return;

      try {
        const appId = getArtifactProjectId();
        const teamRef = doc(db, `artifacts/${appId}/teams/${userTeam}`);
        const teamDoc = await getDoc(teamRef);

        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          
          // Load subscription plan
          const subscription = teamData.subscription || {};
          setSubscriptionPlan(subscription.plan || 'starter');

          // Load lead capture form or use default
          if (teamData.leadCaptureForm) {
            setLeadCaptureForm(teamData.leadCaptureForm);
          }
        }
      } catch (err) {
        console.error('Error loading team settings:', err);
      }
    };

    loadTeamSettings();
  }, [db, userTeam]);

  // Load team member names for agent display (email → full name)
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!db || !userTeam) return;
      try {
        const appId = getArtifactProjectId();
        const usersRef = collection(db, `artifacts/${appId}/teams/${userTeam}/users`);
        const usersSnapshot = await getDocs(usersRef);
        const nameMap = {};
        usersSnapshot.forEach(userDoc => {
          const data = userDoc.data();
          const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ');
          if (fullName) {
            // Map by email (normalized and raw)
            if (data.email) {
              nameMap[data.email] = fullName;
              nameMap[data.email.toLowerCase()] = fullName;
              // Also map by email slug (part before @)
              nameMap[data.email.split('@')[0]] = fullName;
            }
            // Map by document ID (which is the normalized email)
            nameMap[userDoc.id] = fullName;
          }
        });
        setTeamMemberNames(nameMap);
      } catch (err) {
        console.error('Error loading team members:', err);
      }
    };
    loadTeamMembers();
  }, [db, userTeam]);

  // Load evaluations for lead-evaluation linking
  useEffect(() => {
    const loadEvaluations = async () => {
      if (!db || !userTeam) return;
      try {
        const appId = getArtifactProjectId();
        const evalsPath = `artifacts/${appId}/teams/${userTeam}/call-evaluations`;
        const evalsSnapshot = await getDocs(collection(db, evalsPath));
        const evalsMap = {};
        const evalsList = [];
        evalsSnapshot.docs.forEach(evalDoc => {
          const data = evalDoc.data();
          const evalEntry = {
            id: evalDoc.id,
            ...data,
            overallScore: data.evaluation?.overallScore ?? null,
            uploadedAt: data.uploadedAt?.toDate?.() || (data.uploadedAt ? new Date(data.uploadedAt) : null)
          };
          evalsList.push(evalEntry);
          // Map by linked lead ID for quick lookup
          if (data.linkedLeadId) {
            evalsMap[data.linkedLeadId] = evalEntry;
          }
        });
        setEvaluationsMap(evalsMap);
        setEvaluationsList(evalsList);
      } catch (err) {
        console.warn('Error loading evaluations:', err);
      }
    };
    loadEvaluations();
  }, [db, userTeam]);

  // Resolve agent display name from all available sources
  const getAgentDisplayName = useCallback((lead) => {
    // 1. Saved display name from Firebase Auth profile (new leads)
    if (lead.agentDisplayName) return lead.agentDisplayName;
    // 2. Look up from team member records by email
    if (lead.userEmail && teamMemberNames[lead.userEmail]) return teamMemberNames[lead.userEmail];
    // 3. Look up by email slug (e.g. "mphilips1228_gmail_com" or "mphilips1228")
    if (lead.userEmail) {
      const slug = lead.userEmail.split('@')[0];
      if (teamMemberNames[slug]) return teamMemberNames[slug];
      // Try normalized email doc ID format
      const normalized = lead.userEmail.toLowerCase().replace(/[.@]/g, '_');
      if (teamMemberNames[normalized]) return teamMemberNames[normalized];
    }
    // 4. Fallback to email prefix
    if (lead.userEmail) return lead.userEmail.split('@')[0];
    return 'N/A';
  }, [teamMemberNames]);

  // Calculate date range based on time period
  const getDateRange = useCallback(() => {
    const now = new Date();
    let start = null;
    let end = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    switch (timePeriod) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'this_week': {
        const dayOfWeek = now.getDay();
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
        break;
      }
      case 'last_week': {
        const dayOfWeek = now.getDay();
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek - 7);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
        break;
      }
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'this_quarter': {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), currentQuarter * 3, 1);
        break;
      }
      case 'last_quarter': {
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const year = currentQuarter === 0 ? now.getFullYear() - 1 : now.getFullYear();
        start = new Date(year, lastQuarter * 3, 1);
        end = new Date(year, lastQuarter * 3 + 3, 1);
        break;
      }
      case 'this_year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'last_year':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
      default:
        start = null;
        end = null;
    }
    return { start, end };
  }, [timePeriod]);

  // Load leads data
  const loadLeadsData = useCallback(async () => {
    if (!analytics) return;

    setLoading(true);
    setError(null);

    const timeout = setTimeout(() => {
      setLoading(false);
      setError('Loading timed out. Please refresh.');
    }, 15000);

    try {
      const { start, end } = getDateRange();
      const teamFilter = userTeamName;
      let leads = await analytics.getLeadsData(teamFilter, start, end, 0, userTeam);
      
      // Agents only see their own leads
      if (!isAdmin && !isTeamLead) {
        leads = leads.filter(lead => lead.userEmail === userEmail);
      }
      
      setLeadsData(leads);
      clearTimeout(timeout);
      setLoading(false);
    } catch (err) {
      clearTimeout(timeout);
      console.error('Leads load error:', err);
      setError('Failed to load leads. Please try again.');
      setLoading(false);
    }
  }, [analytics, getDateRange, isAdmin, userTeamName, userTeam, userEmail]);

  // Load data when dependencies change
  useEffect(() => { loadLeadsData(); }, [loadLeadsData]);

  // Listen for lead saved events
  useEffect(() => {
    const handleLeadSaved = () => loadLeadsData();
    window.addEventListener('leadSaved', handleLeadSaved);
    return () => window.removeEventListener('leadSaved', handleLeadSaved);
  }, [loadLeadsData]);

  // Get script name from template ID
  const getScriptName = useCallback((lead) => {
    if (lead.templateName) return lead.templateName;
    if (lead.template && availableScripts[lead.template]) {
      return availableScripts[lead.template].name || lead.template;
    }
    return lead.template || 'N/A';
  }, [availableScripts]);

  // Build a field ID → human label mapping from ALL available sources
  const fieldLabelMap = useMemo(() => {
    const map = {};

    // 1. From _fieldLabels saved directly on lead records (most reliable, new leads)
    leadsData.forEach(lead => {
      if (lead._fieldLabels && typeof lead._fieldLabels === 'object') {
        Object.entries(lead._fieldLabels).forEach(([id, label]) => {
          if (id && label) map[id] = label;
        });
      }
    });

    // 2. From team-wide lead capture form (exact ID match)
    if (leadCaptureForm?.fields) {
      leadCaptureForm.fields.forEach(field => {
        if (field.id && field.label) {
          map[field.id] = field.label;
        }
      });
    }

    // 3. From each template's prospect form (exact ID match)
    const templateForms = {}; // templateId → ordered fields array
    Object.entries(availableScripts || {}).forEach(([templateId, template]) => {
      const formSources = [
        template?.prospectForm,
        template?.graph?.prospectForm,
        template?.data?.prospectForm,
        template?.data?.scriptGraph?.prospectForm
      ];
      for (const form of formSources) {
        if (form?.fields && form.fields.length > 0) {
          templateForms[templateId] = form.fields;
          form.fields.forEach(field => {
            if (field.id && field.label) {
              map[field.id] = field.label;
            }
          });
          break; // Use first found form
        }
      }
    });

    // 4. POSITION-BASED MATCHING for old leads with renamed field IDs
    // When field IDs were auto-renamed (old bug), the lead data has the OLD ID
    // but the form config has the NEW ID. Match by position within each template.
    const leadsByTemplate = {};
    leadsData.forEach(lead => {
      if (lead.template) {
        if (!leadsByTemplate[lead.template]) leadsByTemplate[lead.template] = [];
        leadsByTemplate[lead.template].push(lead);
      }
    });

    Object.entries(leadsByTemplate).forEach(([templateId, leads]) => {
      const formFields = templateForms[templateId];
      if (!formFields || formFields.length === 0) return;

      // Get form fields that have labels (these are the ones we want to match)
      const formFieldsWithLabels = formFields.filter(f => f.label);
      if (formFieldsWithLabels.length === 0) return;

      // Collect all data keys from leads of this template that are NOT yet resolved
      // Use the first lead as representative (same template = same form = same keys)
      const representativeLead = leads[0];
      const allLeadKeys = Object.keys(representativeLead).filter(k => !excludedFields.has(k));

      // Find which form field IDs are already present as lead keys (direct match)
      const matchedFormFieldIds = new Set();
      const matchedLeadKeys = new Set();
      formFieldsWithLabels.forEach(ff => {
        if (allLeadKeys.includes(ff.id)) {
          matchedFormFieldIds.add(ff.id);
          matchedLeadKeys.add(ff.id);
        }
      });

      // Unmatched form fields (their IDs were renamed, so they don't appear in leads)
      const unmatchedFormFields = formFieldsWithLabels.filter(ff => !matchedFormFieldIds.has(ff.id));
      // Unmatched lead keys (old IDs that don't match any current form field)
      const unmatchedLeadKeys = allLeadKeys.filter(k => !matchedLeadKeys.has(k) && !map[k]);

      // Match by position: both arrays preserve creation order
      unmatchedLeadKeys.forEach((leadKey, idx) => {
        if (idx < unmatchedFormFields.length && unmatchedFormFields[idx].label) {
          map[leadKey] = unmatchedFormFields[idx].label;
        }
      });
    });

    return map;
  }, [leadsData, leadCaptureForm, availableScripts, excludedFields]);

  // Dynamically detect columns from lead data
  const dynamicColumns = useMemo(() => {
    if (leadsData.length === 0) return [];

    const formatLabel = (key) => {
      // 1. Check the form config label map first (handles all known field IDs)
      if (fieldLabelMap[key]) return fieldLabelMap[key];

      // 2. Check _fieldLabels embedded in any lead that has this key
      for (const lead of leadsData) {
        if (lead._fieldLabels && lead._fieldLabels[key]) {
          return lead._fieldLabels[key];
        }
      }

      // 3. If key already has spaces, use as-is (it's already human-readable)
      if (key.includes(' ')) return key;

      // 4. Strip "Field_" or "field_" prefix if present, then format the remainder
      let rawKey = key.replace(/^[Ff]ield_/, '');

      // 5. Format: underscores → spaces, camelCase → spaces, capitalize each word
      return rawKey
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim() || key;
    };

    const allKeys = new Set();
    leadsData.forEach(lead => {
      Object.keys(lead).forEach(key => {
        if (!excludedFields.has(key)) {
          allKeys.add(key);
        }
      });
    });

    const priorityOrder = ['prospectName', 'Prospect Name', 'prospectPhone', 'Prospect Phone', 'prospectEmail', 'Prospect Email', 'company', 'Company', 'prospectAddress', 'Property Address'];
    
    const columns = Array.from(allKeys).sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a);
      const bIndex = priorityOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    return columns
      .map(key => ({ key, label: formatLabel(key) }))
      .filter(col => col.label !== 'Agent' && col.label !== 'Agent Name');
  }, [leadsData, excludedFields, fieldLabelMap]);

  // Get cell value for a lead and column
  const getCellValue = (lead, columnKey) => {
    const value = lead[columnKey];
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  };

  // --- Data Pipeline: Search → Sort → Paginate ---

  // Debounce search input (3B)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.toLowerCase().trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortColumn, sortDirection, pageSize, selectedScriptFilter, outcomeFilter, temperatureFilter, agentFilter]);

  // Derive unique script names for the filter dropdown
  const scriptFilterOptions = useMemo(() => {
    const names = new Set();
    leadsData.forEach(lead => {
      const name = getScriptName(lead);
      if (name && name !== 'N/A') names.add(name);
    });
    return Array.from(names).sort();
  }, [leadsData, getScriptName]);

  // Derive unique agents from loaded leads (for agent filter dropdown)
  const agentFilterOptions = useMemo(() => {
    const seen = new Set();
    const agents = [];
    leadsData.forEach(lead => {
      const name = getAgentDisplayName(lead);
      const key = lead.userEmail || name;
      if (!seen.has(key)) {
        seen.add(key);
        agents.push({ key, name });
      }
    });
    return agents.sort((a, b) => a.name.localeCompare(b.name));
  }, [leadsData, getAgentDisplayName]);

  // Active filter count (for badge on "Filters" button)
  const activeFilterCount = useMemo(() => [
    outcomeFilter !== 'all',
    temperatureFilter !== 'all',
    agentFilter !== 'all',
  ].filter(Boolean).length, [outcomeFilter, temperatureFilter, agentFilter]);

  // Step 1: Filter leads by all active filters (3B)
  const filteredLeads = useMemo(() => {
    let results = leadsData;
    // Script/template filter
    if (selectedScriptFilter !== 'all') {
      results = results.filter(lead => getScriptName(lead) === selectedScriptFilter);
    }
    // Outcome filter
    if (outcomeFilter !== 'all') {
      results = results.filter(lead => lead.callOutcome === outcomeFilter);
    }
    // Temperature filter (derived from outcome)
    if (temperatureFilter !== 'all') {
      results = results.filter(lead => getTemperature(lead) === temperatureFilter);
    }
    // Agent filter
    if (agentFilter !== 'all') {
      results = results.filter(lead => {
        const key = lead.userEmail || getAgentDisplayName(lead);
        return key === agentFilter;
      });
    }
    // Search text filter
    if (searchTerm) {
      results = results.filter(lead => {
        const dateStr = lead.timestamp?.toLocaleDateString() || '';
        const timeStr = lead.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || '';
        const agentStr = getAgentDisplayName(lead);
        const scriptStr = getScriptName(lead);
        const outcomeStr = lead.callOutcome || '';
        const fixedValues = [dateStr, timeStr, agentStr, scriptStr, outcomeStr];
        const dynamicValues = dynamicColumns.map(col => String(lead[col.key] || ''));
        return [...fixedValues, ...dynamicValues].some(val =>
          val.toLowerCase().includes(searchTerm)
        );
      });
    }
    return results;
  }, [leadsData, searchTerm, selectedScriptFilter, outcomeFilter, temperatureFilter, agentFilter, dynamicColumns, getScriptName, getAgentDisplayName]);

  // Step 2: Sort filtered leads (3A)
  const sortedLeads = useMemo(() => {
    const sorted = [...filteredLeads];
    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'date':
        case 'time':
          aVal = a.timestamp?.getTime() || 0;
          bVal = b.timestamp?.getTime() || 0;
          break;
        case 'agent':
          aVal = getAgentDisplayName(a).toLowerCase();
          bVal = getAgentDisplayName(b).toLowerCase();
          break;
        case 'script':
          aVal = getScriptName(a).toLowerCase();
          bVal = getScriptName(b).toLowerCase();
          break;
        case 'outcome':
          aVal = (a.callOutcome || '').toLowerCase();
          bVal = (b.callOutcome || '').toLowerCase();
          break;
        default:
          aVal = String(a[sortColumn] || '').toLowerCase();
          bVal = String(b[sortColumn] || '').toLowerCase();
          break;
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredLeads, sortColumn, sortDirection, getScriptName, getAgentDisplayName]);

  // Step 3: Paginate sorted leads (3D)
  const paginatedLeads = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedLeads.slice(start, start + pageSize);
  }, [sortedLeads, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedLeads.length / pageSize);

  // Sort handler (3A)
  const handleSort = useCallback((column) => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return column;
      }
      setSortDirection('desc');
      return column;
    });
  }, []);

  // Sort indicator helper (3A)
  const getSortIndicator = useCallback((column) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  }, [sortColumn, sortDirection]);

  // Close detail panel on Escape (3C)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedLead) {
        setSelectedLead(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedLead]);

  // Export leads to CSV
  const exportLeadsToCSV = useCallback(() => {
    if (sortedLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }

    const headers = ['Date', 'Time', 'Agent', 'Script', 'Outcome', 'Temperature', 'Eval Score', ...dynamicColumns.map(col => col.label)];
    const rows = sortedLeads.map(lead => {
      const linkedEval = lead.linkedEvaluationId ? evaluationsMap[lead.id] || evaluationsList.find(e => e.id === lead.linkedEvaluationId) : evaluationsMap[lead.id];
      const temp = getTemperature(lead);
      return [
        lead.timestamp?.toLocaleDateString() || 'N/A',
        lead.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || 'N/A',
        getAgentDisplayName(lead),
        getScriptName(lead),
        lead.callOutcome || '',
        temp ? (temp === 'hot' ? 'Hot' : temp === 'warm' ? 'Warm' : 'Cold') : '',
        linkedEval?.overallScore != null ? linkedEval.overallScore : '',
        ...dynamicColumns.map(col => getCellValue(lead, col.key))
      ];
    });

    const csv = [headers, ...rows].map(row => row.map(f => `"${String(f).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leads-${timePeriod}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Leads exported successfully!');
  }, [sortedLeads, timePeriod, dynamicColumns, getScriptName, getAgentDisplayName]);

  // Start editing form
  const handleStartEditing = () => {
    setEditedFields([...leadCaptureForm.fields]);
    setIsEditingForm(true);
  };

  // Cancel editing
  const handleCancelEditing = () => {
    setEditedFields([]);
    setIsEditingForm(false);
  };

  // Save form changes
  const handleSaveForm = async () => {
    if (!db || !userTeam) return;

    setSavingForm(true);
    try {
      const appId = getArtifactProjectId();
      const teamRef = doc(db, `artifacts/${appId}/teams/${userTeam}`);
      
      const updatedForm = {
        ...leadCaptureForm,
        fields: editedFields,
        updatedAt: new Date(),
        updatedBy: userEmail
      };

      await updateDoc(teamRef, { leadCaptureForm: updatedForm });
      
      setLeadCaptureForm(updatedForm);
      setIsEditingForm(false);
      toast.success('Lead capture form saved!');
    } catch (err) {
      console.error('Error saving form:', err);
      toast.error('Failed to save form. Please try again.');
    } finally {
      setSavingForm(false);
    }
  };

  // Add new field
  const handleAddField = () => {
    // Ensure unique field ID even if Date.now() collides (rapid clicks)
    let baseId = `field_${Date.now()}`;
    const existingIds = new Set(editedFields.map(f => f.id));
    let uniqueId = baseId;
    let counter = 1;
    while (existingIds.has(uniqueId)) {
      uniqueId = `${baseId}_${counter++}`;
    }
    const newField = {
      id: uniqueId,
      label: 'New Field',
      type: 'text',
      required: false
    };
    setEditedFields([...editedFields, newField]);
  };

  // Update field
  const handleUpdateField = (index, key, value) => {
    const updated = [...editedFields];
    updated[index] = { ...updated[index], [key]: value };
    // Only auto-generate ID for brand-new fields that still have the placeholder ID
    if (key === 'label' && updated[index].id && updated[index].id.startsWith('field_')) {
      let candidateId = value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '') || updated[index].id;
      // Ensure generated ID doesn't collide with other fields
      const otherIds = new Set(updated.filter((_, i) => i !== index).map(f => f.id));
      if (otherIds.has(candidateId)) {
        candidateId = `${candidateId}_${Date.now()}`;
      }
      updated[index].id = candidateId;
    }
    setEditedFields(updated);
  };

  // Remove field
  const handleRemoveField = (index) => {
    setEditedFields(editedFields.filter((_, i) => i !== index));
  };

  // Move field up/down
  const handleMoveField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= editedFields.length) return;
    const updated = [...editedFields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setEditedFields(updated);
  };

  // --- Per-script form handlers ---

  // Start editing a script's custom form
  const handleStartScriptFormEdit = (scriptId) => {
    const script = availableScripts[scriptId];
    const existingFields = script?.prospectForm?.fields || [];
    // If no custom form yet, pre-populate with team default fields as starting point
    setScriptEditedFields(existingFields.length > 0 ? [...existingFields] : [...leadCaptureForm.fields]);
    setScriptFormEditing(scriptId);
  };

  // Cancel per-script form editing
  const handleCancelScriptFormEdit = () => {
    setScriptFormEditing(null);
    setScriptEditedFields([]);
  };

  // Save per-script custom form
  const handleSaveScriptForm = async (scriptId) => {
    if (!onUpdateTemplate || !scriptId) return;
    setSavingScriptForm(true);
    try {
      const script = availableScripts[scriptId];
      const templateData = {
        graph: script?.graph || script?.data?.scriptGraph,
        steps: script?.steps,
        prospectForm: {
          title: script?.prospectForm?.title || 'Lead Data',
          fields: scriptEditedFields,
          updatedAt: new Date(),
          updatedBy: userEmail
        }
      };
      await onUpdateTemplate(scriptId, templateData, script?.name);
      setScriptFormEditing(null);
      setScriptEditedFields([]);
      toast.success(`Custom form saved for "${script?.name || scriptId}"!`);
    } catch (err) {
      console.error('Error saving script form:', err);
      toast.error('Failed to save custom form. Please try again.');
    } finally {
      setSavingScriptForm(false);
    }
  };

  // Remove custom form from a script (revert to team default)
  const handleRemoveScriptCustomForm = async (scriptId) => {
    if (!onUpdateTemplate || !scriptId) return;
    setSavingScriptForm(true);
    try {
      const script = availableScripts[scriptId];
      const templateData = {
        graph: script?.graph || script?.data?.scriptGraph,
        steps: script?.steps,
        prospectForm: null // Remove custom form
      };
      await onUpdateTemplate(scriptId, templateData, script?.name);
      toast.success(`"${script?.name || scriptId}" will now use the team default form.`);
    } catch (err) {
      console.error('Error removing script custom form:', err);
      toast.error('Failed to update. Please try again.');
    } finally {
      setSavingScriptForm(false);
    }
  };

  // Per-script field handlers
  const handleAddScriptField = () => {
    const newField = {
      id: `Field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false
    };
    setScriptEditedFields([...scriptEditedFields, newField]);
  };

  const handleUpdateScriptField = (index, key, value) => {
    const updated = [...scriptEditedFields];
    updated[index] = { ...updated[index], [key]: value };
    // Only auto-generate ID for brand-new fields that still have the placeholder ID
    if (key === 'label' && updated[index].id && updated[index].id.startsWith('field_')) {
      updated[index].id = `Field_${value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')}` || updated[index].id;
    }
    setScriptEditedFields(updated);
  };

  const handleRemoveScriptField = (index) => {
    setScriptEditedFields(scriptEditedFields.filter((_, i) => i !== index));
  };

  const handleMoveScriptField = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= scriptEditedFields.length) return;
    const updated = [...scriptEditedFields];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setScriptEditedFields(updated);
  };

  // Get current period label
  const getCurrentPeriodLabel = () => {
    const option = timePeriodOptions.find(o => o.value === timePeriod);
    return option?.label || 'Today';
  };

  // Check if user can edit form (team lead or admin)
  const canEditForm = isAdmin || isTeamLead;

  // Check if on Business plan
  const isProPlus = subscriptionPlan === 'pro_plus';

  // Loading state
  if (loading && leadsData.length === 0) {
    return (
      <div style={{ padding: spacing.md }}>
        <LeadsHeader 
          timePeriod={timePeriod}
          onTimePeriodChange={setTimePeriod}
          timePeriodOptions={timePeriodOptions}
          teamName={userTeamName}
          onExport={exportLeadsToCSV}
          leadsCount={0}
          selectedScriptFilter={selectedScriptFilter}
          onScriptFilterChange={setSelectedScriptFilter}
          scriptFilterOptions={scriptFilterOptions}
        />
        <Card padding="none">
          <div style={{ 
            padding: spacing.md,
            borderBottom: `1px solid ${colors.neutral[100]}`,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <LoadingSpinner size="small" inline message="Loading leads..." />
          </div>
          <SkeletonTable rows={6} columns={5} />
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: spacing.md }}>
        <ErrorMessage
          title="Error Loading Leads"
          message={error}
          severity="error"
          onRetry={loadLeadsData}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: spacing.md }}>
      {/* Header with Time Period Dropdown */}
      <LeadsHeader 
        timePeriod={timePeriod}
        onTimePeriodChange={setTimePeriod}
        timePeriodOptions={timePeriodOptions}
        teamName={userTeamName}
        onExport={exportLeadsToCSV}
        leadsCount={leadsData.length}
        onRefresh={loadLeadsData}
        loading={loading}
        selectedScriptFilter={selectedScriptFilter}
        onScriptFilterChange={setSelectedScriptFilter}
        scriptFilterOptions={scriptFilterOptions}
      />

      {/* Lead Capture Form Configuration - Only for Team Leads/Admins */}
      {canEditForm && (
        <div style={{ marginBottom: spacing.md }}>
          {/* Clickable toggle bar */}
          <button
            onClick={() => setIsFormExpanded(!isFormExpanded)}
            style={{
              width: '100%',
              padding: `${spacing.sm} ${spacing.md}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: isFormExpanded ? colors.primary.main : '#fff',
              color: isFormExpanded ? '#fff' : colors.neutral[800],
              border: `1px solid ${isFormExpanded ? colors.primary.main : colors.neutral[300]}`,
              borderRadius: isFormExpanded ? `${borderRadius.md} ${borderRadius.md} 0 0` : borderRadius.md,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
              boxShadow: shadows.sm
            }}
          >
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm
            }}>
              <span style={{ fontSize: '16px' }}>📋</span>
              <span style={{ 
                fontSize: typography.small.size, 
                fontWeight: 600
              }}>
                Edit Lead Capture Form
              </span>
              <span style={{ 
                fontSize: typography.xs.size, 
                opacity: 0.7
              }}>
                — Configure the form agents see when saving leads
              </span>
            </div>
            <span style={{ 
              transform: isFormExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
              fontSize: '12px'
            }}>
              ▼
            </span>
          </button>

          {isFormExpanded && (
            <div style={{ 
              border: `1px solid ${colors.neutral[200]}`,
              borderTop: 'none',
              borderRadius: `0 0 ${borderRadius.md} ${borderRadius.md}`,
              backgroundColor: '#fff'
            }}>
              {/* Script selector dropdown (replaces tabs) */}
              {isProPlus && Object.keys(availableScripts || {}).length > 0 && (
                <div style={{ 
                  padding: spacing.md, 
                  borderBottom: `1px solid ${colors.neutral[100]}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm
                }}>
                  <label 
                    htmlFor="form-script-select" 
                    style={{ fontSize: typography.small.size, fontWeight: 500, color: colors.neutral[700], whiteSpace: 'nowrap' }}
                  >
                    Viewing form for:
                  </label>
                  <select
                    id="form-script-select"
                    value={activeFormTab}
                    onChange={(e) => { setActiveFormTab(e.target.value); handleCancelEditing(); handleCancelScriptFormEdit(); }}
                    style={{
                      flex: 1,
                      maxWidth: '360px',
                      padding: `${spacing.xs} ${spacing.sm}`,
                      fontSize: typography.small.size,
                      border: `1px solid ${colors.neutral[300]}`,
                      borderRadius: borderRadius.sm,
                      backgroundColor: '#fff',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="default">Team Default (all scripts)</option>
                    {Object.entries(availableScripts).map(([scriptId, script]) => (
                      <option key={scriptId} value={scriptId}>
                        {script?.name || scriptId}{script?.prospectForm?.fields?.length > 0 ? ' (Custom)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Tab content */}
              <div style={{ padding: spacing.md }}>
                {/* --- Team Default Tab --- */}
                {activeFormTab === 'default' && (
                  <div>
                    <div style={{ 
                      fontSize: typography.small.size, 
                      color: colors.neutral[600], 
                      marginBottom: spacing.md,
                      lineHeight: 1.5
                    }}>
                      This is the default lead form used by all scripts{isProPlus ? ' unless a script has its own custom form' : ''}.
                      Agents see these fields when saving a lead.
                    </div>

                    {!isEditingForm ? (
                      <>
                        {leadCaptureForm.fields.map((field, index) => (
                          <div 
                            key={field.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: spacing.sm,
                              padding: spacing.sm,
                              backgroundColor: colors.neutral[50],
                              borderRadius: borderRadius.sm,
                              marginBottom: spacing.xs,
                              border: `1px solid ${colors.neutral[200]}`
                            }}
                          >
                            <span style={{ color: colors.neutral[400], fontSize: typography.small.size, minWidth: '20px' }}>
                              {index + 1}.
                            </span>
                            <span style={{ flex: 1, fontSize: typography.small.size }}>
                              {field.label}
                            </span>
                            <span style={{ 
                              fontSize: typography.xs.size, 
                              color: colors.neutral[500],
                              backgroundColor: colors.neutral[100],
                              padding: '2px 6px',
                              borderRadius: borderRadius.sm
                            }}>
                              {field.type}
                            </span>
                            {field.required && (
                              <span style={{ fontSize: typography.xs.size, color: colors.error.main, fontWeight: 500 }}>
                                Required
                              </span>
                            )}
                          </div>
                        ))}
                        <div style={{ marginTop: spacing.sm }}>
                          <Button variant="outline" size="sm" onClick={handleStartEditing}>
                            Edit Fields
                          </Button>
                        </div>
                      </>
                    ) : (
                      <FormFieldEditor
                        fields={editedFields}
                        onUpdate={handleUpdateField}
                        onRemove={handleRemoveField}
                        onMove={handleMoveField}
                        onAdd={handleAddField}
                        onSave={handleSaveForm}
                        onCancel={handleCancelEditing}
                        saving={savingForm}
                      />
                    )}
                  </div>
                )}

                {/* --- Per-Script Tabs (Business) --- */}
                {isProPlus && activeFormTab !== 'default' && (() => {
                  const scriptId = activeFormTab;
                  const script = availableScripts[scriptId];
                  if (!script) return null;
                  const hasCustomForm = script?.prospectForm?.fields?.length > 0;
                  const isEditing = scriptFormEditing === scriptId;
                  const displayFields = hasCustomForm ? script.prospectForm.fields : leadCaptureForm.fields;

                  return (
                    <div>
                      {/* Toggle: use custom or team default */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: spacing.md,
                        marginBottom: spacing.md,
                        padding: spacing.sm,
                        backgroundColor: hasCustomForm ? (colors.primary[50] || '#EFF6FF') : colors.neutral[50],
                        borderRadius: borderRadius.md,
                        border: `1px solid ${hasCustomForm ? (colors.primary.light || '#93C5FD') : colors.neutral[200]}`
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: typography.small.size, fontWeight: 600, color: colors.neutral[800] }}>
                            {hasCustomForm ? 'Using custom form' : 'Using team default form'}
                          </div>
                          <div style={{ fontSize: typography.xs.size, color: colors.neutral[500], marginTop: '2px' }}>
                            {hasCustomForm 
                              ? 'This script has its own lead capture fields.' 
                              : 'This script uses the same form as all other scripts.'}
                          </div>
                        </div>
                        {hasCustomForm ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveScriptCustomForm(scriptId)}
                            disabled={savingScriptForm}
                          >
                            Switch to Team Default
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleStartScriptFormEdit(scriptId)}
                            disabled={savingScriptForm}
                          >
                            Create Custom Form
                          </Button>
                        )}
                      </div>

                      {/* Show fields (read-only or editing) */}
                      {!isEditing ? (
                        <>
                          <div style={{ 
                            fontSize: typography.xs.size, 
                            color: colors.neutral[500], 
                            marginBottom: spacing.sm,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>
                            {hasCustomForm ? 'Custom fields' : 'Team default fields'} ({displayFields.length})
                          </div>
                          {displayFields.map((field, index) => (
                            <div 
                              key={field.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: spacing.sm,
                                padding: spacing.sm,
                                backgroundColor: colors.neutral[50],
                                borderRadius: borderRadius.sm,
                                marginBottom: spacing.xs,
                                border: `1px solid ${colors.neutral[200]}`
                              }}
                            >
                              <span style={{ color: colors.neutral[400], fontSize: typography.small.size, minWidth: '20px' }}>
                                {index + 1}.
                              </span>
                              <span style={{ flex: 1, fontSize: typography.small.size }}>
                                {field.label}
                              </span>
                              <span style={{ 
                                fontSize: typography.xs.size, 
                                color: colors.neutral[500],
                                backgroundColor: colors.neutral[100],
                                padding: '2px 6px',
                                borderRadius: borderRadius.sm
                              }}>
                                {field.type}
                              </span>
                              {field.required && (
                                <span style={{ fontSize: typography.xs.size, color: colors.error.main, fontWeight: 500 }}>
                                  Required
                                </span>
                              )}
                            </div>
                          ))}
                          {hasCustomForm && (
                            <div style={{ marginTop: spacing.sm }}>
                              <Button variant="outline" size="sm" onClick={() => handleStartScriptFormEdit(scriptId)}>
                                Edit Custom Fields
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <FormFieldEditor
                          fields={scriptEditedFields}
                          onUpdate={handleUpdateScriptField}
                          onRemove={handleRemoveScriptField}
                          onMove={handleMoveScriptField}
                          onAdd={handleAddScriptField}
                          onSave={() => handleSaveScriptForm(scriptId)}
                          onCancel={handleCancelScriptFormEdit}
                          saving={savingScriptForm}
                        />
                      )}
                    </div>
                  );
                })()}

                {/* Non-Business: show upsell for per-script tabs */}
                {!isProPlus && activeFormTab !== 'default' && (
                  <div style={{ textAlign: 'center', padding: spacing.lg, color: colors.neutral[500] }}>
                    Upgrade to Business for per-script custom forms.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leads Table */}
      <Card padding="none">
        {/* Table toolbar: title + search + filter toggle */}
        <div style={{ 
          padding: spacing.md,
          borderBottom: `1px solid ${colors.neutral[100]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: spacing.sm
        }}>
          <h2 style={{ 
            fontSize: typography.h3.size, 
            fontWeight: typography.h3.weight,
            color: colors.neutral[900],
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            📋 {getCurrentPeriodLabel()} Leads
            <span style={{ 
              fontSize: typography.small.size, 
              fontWeight: 400,
              color: colors.neutral[500],
              backgroundColor: colors.neutral[100],
              padding: `2px ${spacing.sm}`,
              borderRadius: borderRadius.full
            }}>
              {sortedLeads.length !== leadsData.length
                ? `${sortedLeads.length} of ${leadsData.length}`
                : leadsData.length
              } {leadsData.length === 1 ? 'lead' : 'leads'}
            </span>
          </h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <span style={{ 
                position: 'absolute', 
                left: spacing.sm, 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: colors.neutral[400],
                fontSize: typography.small.size,
                pointerEvents: 'none'
              }}>
                🔍
              </span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search leads..."
                aria-label="Search leads"
                style={{
                  width: '100%',
                  padding: `${spacing.sm} ${spacing.md}`,
                  paddingLeft: spacing.xl,
                  fontSize: typography.small.size,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: borderRadius.md,
                  outline: 'none',
                  backgroundColor: '#fff',
                  color: colors.neutral[700]
                }}
                onFocus={(e) => { e.target.style.borderColor = colors.primary.main; }}
                onBlur={(e) => { e.target.style.borderColor = colors.neutral[300]; }}
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  aria-label="Clear search"
                  style={{
                    position: 'absolute',
                    right: spacing.sm,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: colors.neutral[400],
                    fontSize: typography.small.size,
                    padding: '2px',
                    lineHeight: 1
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Show Filters toggle button */}
            <button
              onClick={() => setShowFilters(f => !f)}
              aria-expanded={showFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: `${spacing.sm} ${spacing.md}`,
                fontSize: typography.small.size,
                fontWeight: 500,
                border: `1px solid ${activeFilterCount > 0 ? colors.primary.main : colors.neutral[300]}`,
                borderRadius: borderRadius.md,
                backgroundColor: activeFilterCount > 0 ? colors.primary.main : '#fff',
                color: activeFilterCount > 0 ? '#fff' : colors.neutral[700],
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s'
              }}
            >
              <span>⚙</span>
              Filters
              {activeFilterCount > 0 && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: 1
                }}>
                  {activeFilterCount}
                </span>
              )}
              <span style={{ fontSize: '10px', marginLeft: '2px' }}>{showFilters ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>

        {/* Filter Panel (collapsible) */}
        {showFilters && (
          <div style={{
            padding: `${spacing.sm} ${spacing.md}`,
            borderBottom: `1px solid ${colors.neutral[100]}`,
            backgroundColor: colors.neutral[50],
            display: 'flex',
            alignItems: 'flex-end',
            gap: spacing.md,
            flexWrap: 'wrap'
          }}>
            {/* Outcome filter */}
            <div>
              <label style={{ display: 'block', fontSize: typography.xs.size, fontWeight: 600, color: colors.neutral[500], marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Call Outcome
              </label>
              <select
                value={outcomeFilter}
                onChange={(e) => setOutcomeFilter(e.target.value)}
                aria-label="Filter by outcome"
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  fontSize: typography.small.size,
                  border: `1px solid ${outcomeFilter !== 'all' ? colors.primary.main : colors.neutral[300]}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  color: colors.neutral[700],
                  minWidth: '160px'
                }}
              >
                <option value="all">All Outcomes</option>
                {OUTCOME_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Temperature filter */}
            <div>
              <label style={{ display: 'block', fontSize: typography.xs.size, fontWeight: 600, color: colors.neutral[500], marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Temperature
              </label>
              <select
                value={temperatureFilter}
                onChange={(e) => setTemperatureFilter(e.target.value)}
                aria-label="Filter by temperature"
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  fontSize: typography.small.size,
                  border: `1px solid ${temperatureFilter !== 'all' ? colors.primary.main : colors.neutral[300]}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  color: colors.neutral[700],
                  minWidth: '140px'
                }}
              >
                <option value="all">All Temperatures</option>
                <option value="hot">🔥 Hot</option>
                <option value="warm">🌡️ Warm</option>
                <option value="cold">❄️ Cold</option>
              </select>
            </div>

            {/* Agent filter (only for team leads/admins) */}
            {(isAdmin || isTeamLead) && agentFilterOptions.length > 1 && (
              <div>
                <label style={{ display: 'block', fontSize: typography.xs.size, fontWeight: 600, color: colors.neutral[500], marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Agent
                </label>
                <select
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  aria-label="Filter by agent"
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`,
                    fontSize: typography.small.size,
                    border: `1px solid ${agentFilter !== 'all' ? colors.primary.main : colors.neutral[300]}`,
                    borderRadius: borderRadius.sm,
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    color: colors.neutral[700],
                    minWidth: '160px'
                  }}
                >
                  <option value="all">All Agents</option>
                  {agentFilterOptions.map(a => (
                    <option key={a.key} value={a.key}>{a.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Clear all filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setOutcomeFilter('all'); setTemperatureFilter('all'); setAgentFilter('all'); }}
                style={{
                  alignSelf: 'flex-end',
                  padding: `${spacing.xs} ${spacing.sm}`,
                  fontSize: typography.small.size,
                  color: colors.error.main,
                  backgroundColor: '#fff',
                  border: `1px solid ${colors.error.main}`,
                  borderRadius: borderRadius.sm,
                  cursor: 'pointer'
                }}
              >
                ✕ Clear filters
              </button>
            )}
          </div>
        )}
        
        <div style={{ overflowX: 'auto' }}>
          {leadsData.length === 0 && !searchTerm ? (
            <div style={{ 
              textAlign: 'center', 
              color: colors.neutral[500], 
              padding: spacing.xl 
            }}>
              <div style={{ fontSize: '3rem', marginBottom: spacing.sm }}>📭</div>
              <p style={{ fontSize: typography.body.size, marginBottom: spacing.xs }}>
                No leads found for {getCurrentPeriodLabel().toLowerCase()}
              </p>
              <p style={{ fontSize: typography.small.size, color: colors.neutral[400] }}>
                Leads will appear here when agents generate them
              </p>
            </div>
          ) : sortedLeads.length === 0 && searchTerm ? (
            <div style={{ 
              textAlign: 'center', 
              color: colors.neutral[500], 
              padding: spacing.xl 
            }}>
              <div style={{ fontSize: '3rem', marginBottom: spacing.sm }}>🔍</div>
              <p style={{ fontSize: typography.body.size, marginBottom: spacing.xs }}>
                No leads match &ldquo;{searchInput}&rdquo;
              </p>
              <p style={{ fontSize: typography.small.size, color: colors.neutral[400] }}>
                Try a different search term
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.neutral[50] }}>
                  {[
                    { key: 'date', label: 'Date' },
                    { key: 'time', label: 'Time' },
                    { key: 'agent', label: 'Agent' },
                    { key: 'script', label: 'Script' },
                    { key: 'outcome', label: 'Outcome' },
                    { key: 'eval', label: 'Eval' }
                  ].map(col => (
                    <th
                      key={col.key}
                      style={sortableHeaderStyle}
                      onClick={() => handleSort(col.key)}
                      aria-sort={sortColumn === col.key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(col.key); } }}
                    >
                      {col.label}{getSortIndicator(col.key)}
                    </th>
                  ))}
                  {dynamicColumns.map(col => (
                    <th
                      key={col.key}
                      style={sortableHeaderStyle}
                      onClick={() => handleSort(col.key)}
                      aria-sort={sortColumn === col.key ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(col.key); } }}
                    >
                      {col.label}{getSortIndicator(col.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.map((lead, index) => (
                  <tr 
                    key={lead.id || index}
                    onClick={() => setSelectedLead(lead)}
                    style={{ 
                      borderBottom: `1px solid ${colors.neutral[100]}`,
                      backgroundColor: lead.userEmail === userEmail ? colors.primary[50] : 'transparent',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (lead.userEmail !== userEmail) e.currentTarget.style.backgroundColor = colors.neutral[50];
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = lead.userEmail === userEmail ? colors.primary[50] : 'transparent';
                    }}
                  >
                    <td style={tableCellStyle}>
                      {lead.timestamp?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td style={tableCellStyle}>
                      {lead.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || 'N/A'}
                    </td>
                    <td style={tableCellStyle}>
                      {getAgentDisplayName(lead)}
                      {lead.userEmail === userEmail && (
                        <span style={{ color: colors.primary.main, marginLeft: '4px' }}>(You)</span>
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, fontSize: typography.xs.size }}>
                      {getScriptName(lead)}
                    </td>
                    {/* Outcome badge */}
                    <td style={tableCellStyle}>
                      {lead.callOutcome ? (() => {
                        const style = OUTCOME_BADGE[lead.callOutcome] || { bg: colors.neutral[100], color: colors.neutral[600], border: colors.neutral[200] };
                        const shortLabel = lead.callOutcome.length > 14 ? lead.callOutcome.slice(0, 13) + '…' : lead.callOutcome;
                        return (
                          <span
                            title={lead.callOutcome}
                            style={{
                              display: 'inline-block',
                              padding: '2px 7px',
                              borderRadius: '9999px',
                              fontSize: typography.xs.size,
                              fontWeight: 500,
                              color: style.color,
                              backgroundColor: style.bg,
                              border: `1px solid ${style.border}`,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {shortLabel}
                          </span>
                        );
                      })() : <span style={{ color: colors.neutral[300], fontSize: typography.xs.size }}>—</span>}
                    </td>
                    {/* Eval score badge */}
                    <td style={tableCellStyle}>
                      {(() => {
                        const linkedEval = lead.linkedEvaluationId ? evaluationsMap[lead.id] || evaluationsList.find(e => e.id === lead.linkedEvaluationId) : evaluationsMap[lead.id];
                        if (linkedEval && linkedEval.overallScore != null) {
                          const score = linkedEval.overallScore;
                          const scoreColor = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
                          const scoreBg = score >= 80 ? '#ECFDF5' : score >= 60 ? '#FFFBEB' : '#FEF2F2';
                          return (
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '9999px',
                                fontSize: typography.xs.size,
                                fontWeight: 600,
                                color: scoreColor,
                                backgroundColor: scoreBg,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              title={`Evaluation score: ${score}/100`}
                            >
                              {score}
                            </span>
                          );
                        }
                        return (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '2px',
                              color: colors.neutral[300],
                              fontSize: typography.xs.size,
                              cursor: 'pointer'
                            }}
                            title="Click lead to link an evaluation"
                          >
                            🔗
                          </span>
                        );
                      })()}
                    </td>
                    {dynamicColumns.map(col => (
                      <td key={col.key} style={tableCellStyle}>
                        {getCellValue(lead, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls (3D) */}
        {sortedLeads.length > 0 && (
          <div style={{
            padding: `${spacing.sm} ${spacing.md}`,
            borderTop: `1px solid ${colors.neutral[100]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: spacing.sm,
            fontSize: typography.small.size,
            color: colors.neutral[600]
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <span>
                Showing {Math.min((currentPage - 1) * pageSize + 1, sortedLeads.length)}–{Math.min(currentPage * pageSize, sortedLeads.length)} of {sortedLeads.length}
              </span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Rows per page"
                style={{
                  padding: `2px ${spacing.sm}`,
                  fontSize: typography.xs.size,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  color: colors.neutral[700]
                }}
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
                aria-label="Previous page"
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  fontSize: typography.small.size,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: currentPage <= 1 ? colors.neutral[100] : '#fff',
                  color: currentPage <= 1 ? colors.neutral[400] : colors.neutral[700],
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>
              <span style={{ 
                padding: `${spacing.xs} ${spacing.sm}`,
                fontSize: typography.xs.size,
                color: colors.neutral[500]
              }}>
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  fontSize: typography.small.size,
                  border: `1px solid ${colors.neutral[300]}`,
                  borderRadius: borderRadius.sm,
                  backgroundColor: currentPage >= totalPages ? colors.neutral[100] : '#fff',
                  color: currentPage >= totalPages ? colors.neutral[400] : colors.neutral[700],
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Lead Detail Panel (3C) */}
      {selectedLead && (
        <LeadDetailPanel
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          getScriptName={getScriptName}
          getAgentDisplayName={getAgentDisplayName}
          dynamicColumns={dynamicColumns}
          getCellValue={getCellValue}
          userEmail={userEmail}
          evaluationsMap={evaluationsMap}
          evaluationsList={evaluationsList}
          db={db}
          userTeam={userTeam}
          onLeadUpdated={(updatedLead) => {
            // Update the lead in local state so the table reflects the change immediately
            setLeadsData(prev => prev.map(l => l.id === updatedLead.id ? { ...l, ...updatedLead } : l));
            setSelectedLead(prev => prev && prev.id === updatedLead.id ? { ...prev, ...updatedLead } : prev);
          }}
        />
      )}
    </div>
  );
};

/**
 * Reusable Form Field Editor - used for both team default and per-script forms
 */
const FormFieldEditor = ({ fields, onUpdate, onRemove, onMove, onAdd, onSave, onCancel, saving }) => (
  <div>
    <div style={{ marginBottom: spacing.md }}>
      {fields.map((field, index) => (
        <div 
          key={field.id || index}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
            padding: spacing.sm,
            backgroundColor: '#fff',
            borderRadius: borderRadius.sm,
            marginBottom: spacing.sm,
            border: `1px solid ${colors.neutral[200]}`
          }}
        >
          {/* Move buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <button
              onClick={() => onMove(index, -1)}
              disabled={index === 0}
              aria-label={`Move ${field.label} field up`}
              style={{
                padding: '2px 4px',
                fontSize: '10px',
                background: 'none',
                border: 'none',
                cursor: index === 0 ? 'not-allowed' : 'pointer',
                opacity: index === 0 ? 0.3 : 1
              }}
            >
              ▲
            </button>
            <button
              onClick={() => onMove(index, 1)}
              disabled={index === fields.length - 1}
              aria-label={`Move ${field.label} field down`}
              style={{
                padding: '2px 4px',
                fontSize: '10px',
                background: 'none',
                border: 'none',
                cursor: index === fields.length - 1 ? 'not-allowed' : 'pointer',
                opacity: index === fields.length - 1 ? 0.3 : 1
              }}
            >
              ▼
            </button>
          </div>
          
          {/* Label input */}
          <input
            type="text"
            value={field.label}
            onChange={(e) => onUpdate(index, 'label', e.target.value)}
            style={{
              flex: 1,
              padding: '4px 8px',
              fontSize: typography.small.size,
              border: `1px solid ${colors.neutral[300]}`,
              borderRadius: borderRadius.sm
            }}
            placeholder="Field label"
          />
          
          {/* Type select */}
          <select
            value={field.type}
            onChange={(e) => onUpdate(index, 'type', e.target.value)}
            style={{
              padding: '4px 8px',
              fontSize: typography.xs.size,
              border: `1px solid ${colors.neutral[300]}`,
              borderRadius: borderRadius.sm
            }}
          >
            <option value="text">Text</option>
            <option value="tel">Phone</option>
            <option value="email">Email</option>
            <option value="textarea">Long Text</option>
          </select>
          
          {/* Required toggle */}
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            fontSize: typography.xs.size,
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate(index, 'required', e.target.checked)}
            />
            Req
          </label>
          
          {/* Remove button */}
          <button
            onClick={() => onRemove(index)}
            aria-label={`Remove ${field.label} field`}
            style={{
              padding: '4px 8px',
              fontSize: typography.small.size,
              color: colors.error.main,
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>
      ))}
      
      <button
        onClick={onAdd}
        style={{
          width: '100%',
          padding: spacing.sm,
          fontSize: typography.small.size,
          color: colors.primary.main,
          backgroundColor: '#fff',
          border: `1px dashed ${colors.primary.main}`,
          borderRadius: borderRadius.sm,
          cursor: 'pointer'
        }}
      >
        + Add Field
      </button>
    </div>
    
    <div style={{ display: 'flex', gap: spacing.sm }}>
      <Button variant="primary" size="sm" onClick={onSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
      <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
        Cancel
      </Button>
    </div>
  </div>
);

/**
 * Lead Detail Panel - Slide-out side panel showing full lead record (3C)
 */
const LeadDetailPanel = ({ lead, onClose, getScriptName, getAgentDisplayName, dynamicColumns, getCellValue, userEmail, evaluationsMap = {}, evaluationsList = [], db, userTeam, onLeadUpdated }) => {
  const panelRef = useRef(null);
  const [showEvalPicker, setShowEvalPicker] = useState(false);
  const [linkingEval, setLinkingEval] = useState(false);

  // Focus the panel on mount for accessibility
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // Link an evaluation to this lead (bidirectional)
  const handleLinkEvaluation = async (evalId) => {
    if (!db || !userTeam || !evalId || linkingEval) return;
    setLinkingEval(true);
    try {
      const appId = getArtifactProjectId();
      // Find the lead's Firestore path by searching user subcollections
      const teamUsersPath = `artifacts/${appId}/teams/${userTeam}/users`;
      const usersSnapshot = await getDocs(collection(db, teamUsersPath));
      let leadDocRef = null;
      for (const userDoc of usersSnapshot.docs) {
        const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
        const leadsSnapshot = await getDocs(collection(db, leadsPath));
        const match = leadsSnapshot.docs.find(d => d.id === lead.id);
        if (match) {
          leadDocRef = doc(db, `${leadsPath}/${lead.id}`);
          break;
        }
      }
      if (!leadDocRef) throw new Error('Lead document not found');

      // Update lead with evaluation link
      await updateDoc(leadDocRef, { linkedEvaluationId: evalId });
      // Update evaluation with lead link
      const evalDocRef = doc(db, `artifacts/${appId}/teams/${userTeam}/call-evaluations/${evalId}`);
      await updateDoc(evalDocRef, { linkedLeadId: lead.id });

      if (onLeadUpdated) onLeadUpdated({ id: lead.id, linkedEvaluationId: evalId });
      setShowEvalPicker(false);
      toast.success('Evaluation linked to lead');
    } catch (err) {
      console.error('Failed to link evaluation:', err);
      toast.error('Failed to link evaluation');
    } finally {
      setLinkingEval(false);
    }
  };

  // Unlink evaluation from this lead (bidirectional)
  const handleUnlinkEvaluation = async () => {
    if (!db || !userTeam || linkingEval) return;
    const evalId = lead.linkedEvaluationId;
    setLinkingEval(true);
    try {
      const appId = getArtifactProjectId();
      const teamUsersPath = `artifacts/${appId}/teams/${userTeam}/users`;
      const usersSnapshot = await getDocs(collection(db, teamUsersPath));
      let leadDocRef = null;
      for (const userDoc of usersSnapshot.docs) {
        const leadsPath = `${teamUsersPath}/${userDoc.id}/realEstateLeads`;
        const leadsSnapshot = await getDocs(collection(db, leadsPath));
        const match = leadsSnapshot.docs.find(d => d.id === lead.id);
        if (match) {
          leadDocRef = doc(db, `${leadsPath}/${lead.id}`);
          break;
        }
      }
      if (leadDocRef) {
        await updateDoc(leadDocRef, { linkedEvaluationId: null });
      }
      if (evalId) {
        const evalDocRef = doc(db, `artifacts/${appId}/teams/${userTeam}/call-evaluations/${evalId}`);
        await updateDoc(evalDocRef, { linkedLeadId: null });
      }
      if (onLeadUpdated) onLeadUpdated({ id: lead.id, linkedEvaluationId: null });
      toast.success('Evaluation unlinked');
    } catch (err) {
      console.error('Failed to unlink evaluation:', err);
      toast.error('Failed to unlink evaluation');
    } finally {
      setLinkingEval(false);
    }
  };

  // Format a key name into a readable label
  const formatKey = (key) => {
    if (key.includes(' ')) return key;
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  return (
    <>
      {/* Backdrop overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: zIndex.modalBackdrop
        }}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Lead details"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '400px',
          maxWidth: '90vw',
          height: '100vh',
          backgroundColor: '#fff',
          boxShadow: shadows.xl,
          zIndex: zIndex.modal,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none'
        }}
      >
        {/* Header */}
        <div style={{
          padding: spacing.md,
          borderBottom: `1px solid ${colors.neutral[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: typography.h3.size, 
            fontWeight: typography.h3.weight,
            color: colors.neutral[900]
          }}>
            Lead Details
          </h3>
          <button
            onClick={onClose}
            aria-label="Close detail panel"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: colors.neutral[400],
              padding: spacing.xs,
              borderRadius: borderRadius.sm,
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: spacing.md }}>
          {/* Meta info */}
          <div style={{ marginBottom: spacing.md }}>
            <DetailRow label="Date" value={lead.timestamp?.toLocaleDateString() || 'N/A'} />
            <DetailRow label="Time" value={lead.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || 'N/A'} />
            <DetailRow
              label="Agent"
              value={
                <>
                  {getAgentDisplayName(lead)}
                  {lead.userEmail === userEmail && (
                    <span style={{ color: colors.primary.main, marginLeft: '4px' }}>(You)</span>
                  )}
                </>
              }
            />
            <DetailRow label="Script" value={getScriptName(lead)} />
            {lead.callOutcome && (
              <DetailRow label="Outcome" value={lead.callOutcome} />
            )}
          </div>

          {/* Evaluation Link */}
          <div style={{ marginBottom: spacing.md }}>
            <h4 style={{ 
              fontSize: typography.small.size, 
              fontWeight: 600, 
              color: colors.neutral[500],
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: spacing.sm 
            }}>
              Evaluation
            </h4>
            {(() => {
              const linkedEval = lead.linkedEvaluationId 
                ? evaluationsMap[lead.id] || evaluationsList.find(e => e.id === lead.linkedEvaluationId) 
                : evaluationsMap[lead.id];
              if (linkedEval && linkedEval.overallScore != null) {
                const score = linkedEval.overallScore;
                const scoreColor = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
                const scoreBg = score >= 80 ? '#ECFDF5' : score >= 60 ? '#FFFBEB' : '#FEF2F2';
                return (
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.sm,
                      padding: spacing.sm,
                      backgroundColor: scoreBg,
                      borderRadius: borderRadius.md,
                      border: `1px solid ${scoreColor}20`
                    }}>
                      <span style={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: scoreColor
                      }}>
                        {score}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: typography.xs.size, color: colors.neutral[500] }}>
                          Overall Score
                        </div>
                        <div style={{ fontSize: typography.xs.size, color: colors.neutral[600] }}>
                          {linkedEval.salesperson || 'Unknown'} — {linkedEval.uploadedAt?.toLocaleDateString() || 'N/A'}
                        </div>
                      </div>
                      <button
                        onClick={handleUnlinkEvaluation}
                        disabled={linkingEval}
                        title="Unlink evaluation"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: linkingEval ? 'not-allowed' : 'pointer',
                          color: colors.neutral[400],
                          fontSize: typography.xs.size,
                          padding: spacing.xs,
                          borderRadius: borderRadius.sm,
                          opacity: linkingEval ? 0.5 : 1
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              }
              // No evaluation linked — show link button/picker
              return (
                <div>
                  {showEvalPicker ? (
                    <div style={{ 
                      border: `1px solid ${colors.neutral[200]}`,
                      borderRadius: borderRadius.md,
                      padding: spacing.sm
                    }}>
                      <div style={{ fontSize: typography.xs.size, fontWeight: 500, color: colors.neutral[600], marginBottom: spacing.xs }}>
                        Select an evaluation to link:
                      </div>
                      <select
                        onChange={(e) => { if (e.target.value) handleLinkEvaluation(e.target.value); }}
                        disabled={linkingEval}
                        style={{
                          width: '100%',
                          padding: `${spacing.xs} ${spacing.sm}`,
                          fontSize: typography.xs.size,
                          border: `1px solid ${colors.neutral[300]}`,
                          borderRadius: borderRadius.sm,
                          backgroundColor: '#fff',
                          cursor: linkingEval ? 'not-allowed' : 'pointer'
                        }}
                        defaultValue=""
                      >
                        <option value="" disabled>Choose evaluation...</option>
                        {evaluationsList
                          .filter(ev => ev.overallScore != null && !ev.linkedLeadId)
                          .map(ev => (
                            <option key={ev.id} value={ev.id}>
                              {ev.salesperson || 'Unknown'} — {ev.overallScore}/100 — {ev.uploadedAt?.toLocaleDateString() || 'N/A'}
                            </option>
                          ))
                        }
                      </select>
                      {linkingEval && (
                        <div style={{ fontSize: typography.xs.size, color: colors.primary.main, marginTop: spacing.xs }}>
                          Linking...
                        </div>
                      )}
                      <button
                        onClick={() => setShowEvalPicker(false)}
                        disabled={linkingEval}
                        style={{
                          marginTop: spacing.xs,
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: typography.xs.size,
                          color: colors.neutral[400]
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowEvalPicker(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: spacing.xs,
                        width: '100%',
                        padding: spacing.sm,
                        border: `1px dashed ${colors.neutral[300]}`,
                        borderRadius: borderRadius.md,
                        backgroundColor: colors.neutral[50],
                        cursor: 'pointer',
                        fontSize: typography.small.size,
                        color: colors.neutral[500],
                        transition: 'all 0.15s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.primary.main; e.currentTarget.style.color = colors.primary.main; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.neutral[300]; e.currentTarget.style.color = colors.neutral[500]; }}
                    >
                      <span style={{ fontSize: '1rem' }}>🔗</span>
                      Link an evaluation
                    </button>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Collected Data (dynamic fields) */}
          {dynamicColumns.length > 0 && (
            <div style={{ marginBottom: spacing.md }}>
              <h4 style={{ 
                fontSize: typography.small.size, 
                fontWeight: 600, 
                color: colors.neutral[500],
                textTransform: 'uppercase',
                marginBottom: spacing.sm,
                letterSpacing: '0.05em',
                margin: `0 0 ${spacing.sm} 0`
              }}>
                Collected Data
              </h4>
              {dynamicColumns.map(col => (
                <DetailRow key={col.key} label={col.label} value={getCellValue(lead, col.key)} />
              ))}
            </div>
          )}

          {/* Property Details */}
          {lead.propertyDetails && typeof lead.propertyDetails === 'object' && Object.keys(lead.propertyDetails).length > 0 && (
            <div style={{ marginBottom: spacing.md }}>
              <h4 style={{ 
                fontSize: typography.small.size, 
                fontWeight: 600, 
                color: colors.neutral[500],
                textTransform: 'uppercase',
                marginBottom: spacing.sm,
                letterSpacing: '0.05em',
                margin: `0 0 ${spacing.sm} 0`
              }}>
                Property Details
              </h4>
              {Object.entries(lead.propertyDetails).map(([key, value]) => (
                <DetailRow key={key} label={formatKey(key)} value={String(value ?? '—')} />
              ))}
            </div>
          )}

          {/* Conversation History */}
          {lead.conversationHistory && Array.isArray(lead.conversationHistory) && lead.conversationHistory.length > 0 && (
            <div style={{ marginBottom: spacing.md }}>
              <h4 style={{ 
                fontSize: typography.small.size, 
                fontWeight: 600, 
                color: colors.neutral[500],
                textTransform: 'uppercase',
                marginBottom: spacing.sm,
                letterSpacing: '0.05em',
                margin: `0 0 ${spacing.sm} 0`
              }}>
                Conversation History
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {lead.conversationHistory.map((entry, i) => {
                  const role = entry.role || entry.speaker || 'message';
                  const text = entry.text || entry.message || entry.content || (typeof entry === 'string' ? entry : JSON.stringify(entry));
                  const isAgent = role.toLowerCase() === 'agent' || role.toLowerCase() === 'user';
                  return (
                    <div
                      key={i}
                      style={{
                        padding: spacing.sm,
                        backgroundColor: isAgent ? colors.primary[50] : colors.neutral[50],
                        borderRadius: borderRadius.md,
                        fontSize: typography.small.size,
                        lineHeight: 1.5
                      }}
                    >
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: typography.xs.size,
                        color: isAgent ? colors.primary.dark : colors.neutral[600],
                        marginBottom: '2px',
                        textTransform: 'capitalize'
                      }}>
                        {role}
                      </div>
                      <div style={{ color: colors.neutral[700] }}>
                        {text}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * Detail Row - Key-value display row for the detail panel
 */
const DetailRow = ({ label, value }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: `${spacing.xs} 0`,
    borderBottom: `1px solid ${colors.neutral[100]}`,
    gap: spacing.sm
  }}>
    <span style={{ 
      fontSize: typography.small.size, 
      color: colors.neutral[500],
      flexShrink: 0,
      minWidth: '100px'
    }}>
      {label}
    </span>
    <span style={{ 
      fontSize: typography.small.size, 
      color: colors.neutral[800],
      textAlign: 'right',
      wordBreak: 'break-word'
    }}>
      {value}
    </span>
  </div>
);

/**
 * Leads Header with Time Period Dropdown
 */
const LeadsHeader = ({ 
  timePeriod, 
  onTimePeriodChange, 
  timePeriodOptions, 
  teamName, 
  onExport, 
  leadsCount,
  onRefresh,
  loading,
  selectedScriptFilter,
  onScriptFilterChange,
  scriptFilterOptions
}) => {
  return (
    <div 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: spacing.md,
        paddingBottom: spacing.sm,
        borderBottom: `1px solid ${colors.neutral[200]}`,
        flexWrap: 'wrap',
        gap: spacing.sm
      }}
    >
      <div>
        {teamName && (
          <p style={{ 
            fontSize: typography.small.size, 
            color: colors.neutral[500],
            margin: 0
          }}>
            {teamName}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
        <select
          value={timePeriod}
          onChange={(e) => onTimePeriodChange(e.target.value)}
          style={{
            padding: `${spacing.sm} ${spacing.md}`,
            fontSize: typography.small.size,
            fontWeight: 500,
            backgroundColor: '#ffffff',
            color: colors.neutral[700],
            border: `1px solid ${colors.neutral[300]}`,
            borderRadius: borderRadius.md,
            cursor: 'pointer',
            outline: 'none',
            minWidth: '140px'
          }}
          aria-label="Select time period"
        >
          {timePeriodOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {scriptFilterOptions && scriptFilterOptions.length > 0 && (
          <select
            value={selectedScriptFilter || 'all'}
            onChange={(e) => onScriptFilterChange && onScriptFilterChange(e.target.value)}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: typography.small.size,
              fontWeight: 500,
              backgroundColor: '#ffffff',
              color: colors.neutral[700],
              border: `1px solid ${colors.neutral[300]}`,
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              outline: 'none',
              minWidth: '150px'
            }}
            aria-label="Filter by script"
          >
            <option value="all">All Scripts</option>
            {scriptFilterOptions.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}

        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              padding: spacing.sm,
              backgroundColor: 'transparent',
              border: `1px solid ${colors.neutral[200]}`,
              borderRadius: borderRadius.md,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.5 : 1
            }}
            title="Refresh data"
            aria-label="Refresh leads data"
          >
            <span style={{ 
              display: 'inline-block',
              animation: loading ? 'spin 1s linear infinite' : 'none'
            }}>
              🔄
            </span>
          </button>
        )}

        <span title={leadsCount === 0 ? 'Export is available once leads are loaded.' : undefined}>
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            disabled={leadsCount === 0}
          >
            📥 Export CSV
          </Button>
        </span>
      </div>
    </div>
  );
};

// Table styles
const tableHeaderStyle = {
  padding: `${spacing.sm} ${spacing.md}`,
  fontSize: typography.xs.size,
  fontWeight: 600,
  color: colors.neutral[500],
  textTransform: 'uppercase',
  textAlign: 'left',
  borderBottom: `1px solid ${colors.neutral[200]}`,
  whiteSpace: 'nowrap'
};

const sortableHeaderStyle = {
  ...tableHeaderStyle,
  cursor: 'pointer',
  userSelect: 'none'
};

const tableCellStyle = {
  padding: `${spacing.sm} ${spacing.md}`,
  fontSize: typography.small.size,
  color: colors.neutral[700]
};

export default Leads;
